import { useEffect, useRef } from "react";
import type { CanvasEdge, CanvasNode } from "@opus-dt/shared-types";
import type { LineCanvas } from "@/types/canvas.types";
import { getDraftStore } from "@/features/lines/draft/useLineDraftState";

// Espelha o canvas EM EDIÇÃO para fora do componente, para os dois consumidores que precisam dele
// e vivem acima na árvore. Os dois caminhos são mutuamente exclusivos:
//
//   · linha em RASCUNHO (`isDraft`) → o store zustand da criação, que é quem salva no fim;
//   · linha REAL em edição          → o painel de pendências de `LineEditorUnified`.
//
// ⚠️ A guarda `lastWritten*Ref` com `JSON.stringify` não é otimização. Os dois destinos disparam
// `setState` acima na árvore, e sem comparar o CONTEÚDO cada re-render republicaria, o pai
// re-renderizaria e o ciclo se fecharia. Esta tela já teve um loop "Maximum update depth exceeded"
// nascido de identidade instável; a comparação por valor é o que garante que só mudança de fato
// atravessa.

interface Params {
  isDraft: boolean;
  draftCanvasNodes: CanvasNode[];
  draftCanvasEdges: CanvasEdge[];
  /**
   * O canvas DO SERVIDOR, do qual o rascunho é semeado. Só serve para saber se o rascunho já foi
   * semeado — ver a trava abaixo.
   */
  serverCanvas?: LineCanvas;
  /**
   * Publica o canvas em edição de uma linha REAL. Presente só no editor unificado; o canvas é
   * usado em outras telas (detalhe da linha, ambiente) que não têm painel de pendências.
   */
  onEditCanvasChange?: (canvas: LineCanvas) => void;
}

export function useDraftCanvasMirror({
  isDraft,
  draftCanvasNodes,
  draftCanvasEdges,
  serverCanvas,
  onEditCanvasChange,
}: Params) {
  const lastWrittenRef = useRef<string>("");
  const lastPublishedRef = useRef<string>("");
  const seededRef = useRef(false);

  // ── Linha real em edição → painel de pendências ────────────────────────────────────────────
  useEffect(() => {
    if (isDraft || !onEditCanvasChange) return;

    // ⚠️ A TRAVA DE SEMEADURA. `useCanvasDraft` começa com `[]` e semeia num efeito declarado
    // ANTES deste, então existe um commit em que o canvas do servidor já chegou e o rascunho
    // ainda está vazio. Publicar ali mandava `{nodes: [], edges: []}` para o painel, que emitia
    // "máquina sem representação no canvas" para TODAS as máquinas — pendências fantasma que o
    // ciclo seguinte marca como concluídas e que ficam na lista para sempre.
    //
    // Pior: se a leitura do canvas FALHA (401, rede), o rascunho nunca é semeado e as fantasmas
    // ficam PENDENTES — uma falha de leitura desenhada como "sua linha tem N problemas", que é
    // exatamente a inversão que a regra de erro do repositório proíbe.
    //
    // A trava vale só para essa janela inicial: depois de semeado, esvaziar o canvas na tela é
    // uma edição legítima e PRECISA ser publicada.
    if (!seededRef.current) {
      if (!serverCanvas) return;
      if (draftCanvasNodes.length === 0 && serverCanvas.nodes.length > 0) return;
      seededRef.current = true;
    }

    const serialized = JSON.stringify({ n: draftCanvasNodes, e: draftCanvasEdges });
    if (serialized === lastPublishedRef.current) return;
    lastPublishedRef.current = serialized;
    // `viewport` e `isFallback` não participam da validação — o painel só lê nós e arestas.
    // `isFallback: false` é uma afirmação correta: o que está em edição não é o layout inferido.
    onEditCanvasChange({
      nodes: draftCanvasNodes,
      edges: draftCanvasEdges,
      viewport: null,
      isFallback: false,
    });
  }, [isDraft, draftCanvasNodes, draftCanvasEdges, serverCanvas, onEditCanvasChange]);

  useEffect(() => {
    if (!isDraft) return;
    const draftNodes = draftCanvasNodes.map((n) => ({
      tmpId: ensureTmpId(n.id, "node"),
      type: n.type,
      label: n.label,
      x: n.x,
      y: n.y,
      machineTmpId:
        n.type === "MACHINE" && typeof n.data?.machineId === "string" ? n.data.machineId : null,
      data: stripMachineId(n.data ?? {}),
    }));
    const draftEdges = draftCanvasEdges.map((e) => ({
      tmpId: ensureTmpId(e.id, "edge"),
      sourceNodeTmpId: e.sourceNodeId as `tmp_node_${string}`,
      targetNodeTmpId: e.targetNodeId as `tmp_node_${string}`,
      type: e.type,
      data: e.data ?? {},
    }));

    const next = { nodes: draftNodes, edges: draftEdges };
    const serialized = JSON.stringify(next);
    if (serialized === lastWrittenRef.current) return;
    lastWrittenRef.current = serialized;
    getDraftStore().getState().setCanvas(next);
  }, [isDraft, draftCanvasNodes, draftCanvasEdges]);
}

function ensureTmpId<K extends "node" | "edge">(id: string, kind: K): `tmp_${K}_${string}` {
  const prefix = `tmp_${kind}_` as const;
  if (id.startsWith(prefix)) return id as `tmp_${K}_${string}`;
  const uuid = id.replace(/^tmp_[a-z_]+_/, "");
  return `${prefix}${uuid}`;
}

function stripMachineId(data: Record<string, unknown>): Record<string, unknown> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destruct para excluir machineId (vive no machineTmpId)
  const { machineId, ...rest } = data;
  return rest;
}
